import { useInventoryStore } from 'stores/inventory-store'
import { Message } from '@stockpulse/typescript-axios'

export type MatchedTitle = {
    name: string | undefined
    id: number
    icon: string
}

export type EnrichedMessage = (Message & {
  matchedTitles: MatchedTitle[];
  keyEvents: {
    name: string
    icon: string | undefined
    id: number
  }[];
  sub: string | undefined
  body: string | undefined
  sourceIcon: string,
  sourceLabel: string
})

export function getRelevantMatchedTitles (titles: MatchedTitle[]): MatchedTitle[] {
  return titles.filter(title => useInventoryStore().getTitleById(title.id)?.type !== 'class')
}

export function getEnrichedMessage (message: Message) : EnrichedMessage {
  const inventoryStore = useInventoryStore()
  const titles = message.titles
    ? message.titles.map(title => {
      const titleInfo = inventoryStore.getTitleById(title.title_id)
      return {
        name: titleInfo?.n,
        id: title.title_id,
        icon: titleInfo?.icon || 'spi-building'
      }
    }) : []

  let keyEvents: Array<{ name: string, icon: string | undefined, id: number }> = []
  if (message.key_events_v2 !== undefined) {
    keyEvents = Object.keys(message.key_events_v2)
      .filter(keyEventId => message.key_events_v2 ? message.key_events_v2[keyEventId] >= 4 : false)
      // .sort((a, b) => message.key_events_v2[a] - message.key_events_v2[b])
      .slice(0, 5)
      .map(keyEventId => {
        return {
          name: inventoryStore.translateKeyEvent(parseInt(keyEventId)),
          icon: inventoryStore.getKeyEventIconById(parseInt(keyEventId)),
          id: parseInt(keyEventId)
        }
      })
  }

  let subject = (message.type === 'tweets' ? message.url : message.sub)?.replace(/<\/?[^>]+(>|$)/g, '')
  let body = (message.type === 'tweets' ? message.sub : message.body)?.replace(/<\/?[^>]+(>|$)/g, '')

  // consider type of message (tweets, forum_posts, news, comments, adhoc)
  if (['forum_posts', 'comments'].includes(message.type)) {
    // switch body and subject for nicer display of forum posts
    if (body) {
      subject = body
      body = message.sub || ''
    }
  }

  if ((subject === '' || subject == null || subject === 'null') && body != null && body !== '' && body !== 'null') {
    subject = body
    body = ''
  }
  const sourceIcon = inventoryStore.getSourceIconByName(message.so)
  let sourceLabel = message.so
  if (message.so === 'twitter.com') {
    sourceLabel = 'x.com'
  }

  return {
    ...message,
    matchedTitles: titles,
    keyEvents,
    sub: subject || '',
    body: body || '',
    sourceIcon,
    sourceLabel
  }
}

export const FILING_TYPES = 'filings,adhoc,adv_fin_report,annual_meeting,cap_mkt_info' +
  ',dir_dealings,nav,regulatory,short_interest,takeover_offer'

export interface AuthorType {
  value: string,
  label: string
}

export interface MessageLanguage {
  value: string,
  label: string,
  icon?: string
}

export interface MessageType {
  value: string,
  label: string,
  icon?: string
}

export interface MessageSentiment {
  value: string,
  label: string,
  icon?: string
}

export const messageTypes : MessageType[] = [
  { value: 'comments', label: 'Comments', icon: 'spi-comments' },
  { value: 'forum_posts', label: 'Forum Posts', icon: 'spi-envelopes-bulk' },
  { value: 'tweets', label: 'Tweets', icon: 'spi-twitter' },
  { value: 'videos', label: 'Videos', icon: 'spi-video' },
  { value: 'blog', label: 'Blog', icon: 'spi-blog' },
  { value: 'news', label: 'News', icon: 'spi-newspaper' },
  { value: 'analyst', label: 'Analyst', icon: 'spi-magnifying-glass-chart' },
  // TODO_NEXT This is a hack for MessageList
  {
    value: FILING_TYPES,
    label: 'Filings',
    icon: 'spi-envelope-open-text'
  }
]

export const messageSentiments : MessageSentiment[] = [{
  label: 'Positive',
  value: 'positive',
  icon: 'spi-face-grin'
}, {
  label: 'Neutral',
  value: 'neutral',
  icon: 'spi-face-meh'
}, {
  label: 'Negative',
  value: 'negative',
  icon: 'spi-face-frown'
}]

export const authorTypes : AuthorType[] = [
  { value: 'videos', label: 'Videos' },
  { value: 'videos', label: 'Videos' }
]

export const messageLanguages: MessageLanguage[] = [
  { value: 'ab', label: 'Abkhazian' },
  { value: 'aa', label: 'Afar' },
  { value: 'af', label: 'Afrikaans' },
  { value: 'ak', label: 'Akan' },
  { value: 'sq', label: 'Albanian' },
  { value: 'am', label: 'Amharic' },
  { value: 'ar', label: 'Arabic' },
  { value: 'an', label: 'Aragonese' },
  { value: 'hy', label: 'Armenian' },
  { value: 'as', label: 'Assamese' },
  { value: 'av', label: 'Avaric' },
  { value: 'ae', label: 'Avestan' },
  { value: 'ay', label: 'Aymara' },
  { value: 'az', label: 'Azerbaijani' },
  { value: 'bm', label: 'Bambara' },
  { value: 'ba', label: 'Bashkir' },
  { value: 'eu', label: 'Basque' },
  { value: 'be', label: 'Belarusian' },
  { value: 'bn', label: 'Bengali' },
  { value: 'bi', label: 'Bislama' },
  { value: 'bs', label: 'Bosnian' },
  { value: 'br', label: 'Breton' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'my', label: 'Burmese' },
  { value: 'ca', label: 'Catalan, Valencian' },
  { value: 'ch', label: 'Chamorro' },
  { value: 'ce', label: 'Chechen' },
  { value: 'ny', label: 'Chichewa, Chewa, Nyanja' },
  { value: 'zh', label: 'Chinese (zh)' },
  { value: 'cn', label: 'Chinese (cn)' },
  { value: 'cu', label: 'Church Slavonic, Old Slavonic, Old Church Slavonic' },
  { value: 'cv', label: 'Chuvash' },
  { value: 'kw', label: 'Cornish' },
  { value: 'co', label: 'Corsican' },
  { value: 'cr', label: 'Cree' },
  { value: 'hr', label: 'Croatian' },
  { value: 'cs', label: 'Czech' },
  { value: 'da', label: 'Danish' },
  { value: 'dv', label: 'Divehi, Dhivehi, Maldivian' },
  { value: 'nl', label: 'Dutch, Flemish' },
  { value: 'dz', label: 'Dzongkha' },
  { value: 'en', label: 'English' },
  { value: 'eo', label: 'Esperanto' },
  { value: 'et', label: 'Estonian' },
  { value: 'ee', label: 'Ewe' },
  { value: 'fo', label: 'Faroese' },
  { value: 'fj', label: 'Fijian' },
  { value: 'fi', label: 'Finnish' },
  { value: 'fr', label: 'French' },
  { value: 'fy', label: 'Western Frisian' },
  { value: 'ff', label: 'Fulah' },
  { value: 'gd', label: 'Gaelic, Scottish Gaelic' },
  { value: 'gl', label: 'Galician' },
  { value: 'lg', label: 'Ganda' },
  { value: 'ka', label: 'Georgian' },
  { value: 'de', label: 'German' },
  { value: 'el', label: 'Greek: Modern' },
  { value: 'kl', label: 'Kalaallisut, Greenlandic' },
  { value: 'gn', label: 'Guarani' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'ht', label: 'Haitian, Haitian Creole' },
  { value: 'ha', label: 'Hausa' },
  { value: 'he', label: 'Hebrew' },
  { value: 'hz', label: 'Herero' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ho', label: 'Hiri Motu' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'is', label: 'Icelandic' },
  { value: 'io', label: 'Ido' },
  { value: 'ig', label: 'Igbo' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ia', label: 'Interlingua' },
  { value: 'ie', label: 'Interlingue, Occidental' },
  { value: 'iu', label: 'Inuktitut' },
  { value: 'ik', label: 'Inupiaq' },
  { value: 'ga', label: 'Irish' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'jv', label: 'Javanese' },
  { value: 'kn', label: 'Kannada' },
  { value: 'kr', label: 'Kanuri' },
  { value: 'ks', label: 'Kashmiri' },
  { value: 'kk', label: 'Kazakh' },
  { value: 'km', label: 'Central Khmer' },
  { value: 'ki', label: 'Kikuyu, Gikuyu' },
  { value: 'rw', label: 'Kinyarwanda' },
  { value: 'ky', label: 'Kirghiz, Kyrgyz' },
  { value: 'kv', label: 'Komi' },
  { value: 'kg', label: 'Kongo' },
  { value: 'ko', label: 'Korean' },
  { value: 'kj', label: 'Kuanyama, Kwanyama' },
  { value: 'ku', label: 'Kurdish' },
  { value: 'lo', label: 'Lao' },
  { value: 'la', label: 'Latin' },
  { value: 'lv', label: 'Latvian' },
  { value: 'li', label: 'Limburgan, Limburger, Limburgish' },
  { value: 'ln', label: 'Lingala' },
  { value: 'lt', label: 'Lithuanian' },
  { value: 'lu', label: 'Luba-Katanga' },
  { value: 'lb', label: 'Luxembourgish, Letzeburgesch' },
  { value: 'mk', label: 'Macedonian' },
  { value: 'mg', label: 'Malagasy' },
  { value: 'ms', label: 'Malay' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'mt', label: 'Maltese' },
  { value: 'gv', label: 'Manx' },
  { value: 'mi', label: 'Maori' },
  { value: 'mr', label: 'Marathi' },
  { value: 'mh', label: 'Marshallese' },
  { value: 'mn', label: 'Mongolian' },
  { value: 'na', label: 'Nauru' },
  { value: 'nv', label: 'Navajo: Navaho' },
  { value: 'nd', label: 'North Ndebele' },
  { value: 'nr', label: 'South Ndebele' },
  { value: 'ng', label: 'Ndonga' },
  { value: 'ne', label: 'Nepali' },
  { value: 'no', label: 'Norwegian' },
  { value: 'nb', label: 'Norwegian Bokmål' },
  { value: 'nn', label: 'Norwegian Nynorsk' },
  { value: 'ii', label: 'Sichuan Yi, Nuosu' },
  { value: 'oc', label: 'Occitan' },
  { value: 'oj', label: 'Ojibwa' },
  { value: 'or', label: 'Oriya' },
  { value: 'om', label: 'Oromo' },
  { value: 'os', label: 'Ossetian, Ossetic' },
  { value: 'pi', label: 'Pali' },
  { value: 'ps', label: 'Pashto, Pushto' },
  { value: 'fa', label: 'Persian' },
  { value: 'pl', label: 'Polish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'pa', label: 'Punjabi, Panjabi' },
  { value: 'qu', label: 'Quechua' },
  { value: 'ro', label: 'Romanian, Moldavian, Moldovan' },
  { value: 'rm', label: 'Romansh' },
  { value: 'rn', label: 'Rundi' },
  { value: 'ru', label: 'Russian' },
  { value: 'se', label: 'Northern Sami' },
  { value: 'sm', label: 'Samoan' },
  { value: 'sg', label: 'Sango' },
  { value: 'sa', label: 'Sanskrit' },
  { value: 'sc', label: 'Sardinian' },
  { value: 'sr', label: 'Serbian' },
  { value: 'sn', label: 'Shona' },
  { value: 'sd', label: 'Sindhi' },
  { value: 'si', label: 'Sinhala, Sinhalese' },
  { value: 'sk', label: 'Slovak' },
  { value: 'sl', label: 'Slovenian' },
  { value: 'so', label: 'Somali' },
  { value: 'st', label: 'Southern Sotho' },
  { value: 'es', label: 'Spanish: Castilian' },
  { value: 'su', label: 'Sundanese' },
  { value: 'sw', label: 'Swahili' },
  { value: 'ss', label: 'Swati' },
  { value: 'sv', label: 'Swedish' },
  { value: 'tl', label: 'Tagalog' },
  { value: 'ty', label: 'Tahitian' },
  { value: 'tg', label: 'Tajik' },
  { value: 'ta', label: 'Tamil' },
  { value: 'tt', label: 'Tatar' },
  { value: 'te', label: 'Telugu' },
  { value: 'th', label: 'Thai' },
  { value: 'bo', label: 'Tibetan' },
  { value: 'ti', label: 'Tigrinya' },
  { value: 'to', label: 'Tonga (Tonga Islands)' },
  { value: 'ts', label: 'Tsonga' },
  { value: 'tn', label: 'Tswana' },
  { value: 'tr', label: 'Turkish' },
  { value: 'tk', label: 'Turkmen' },
  { value: 'tw', label: 'Twi' },
  { value: 'ug', label: 'Uighur, Uyghur' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ur', label: 'Urdu' },
  { value: 'uz', label: 'Uzbek' },
  { value: 've', label: 'Venda' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'vo', label: 'Volapük' },
  { value: 'wa', label: 'Walloon' },
  { value: 'cy', label: 'Welsh' },
  { value: 'wo', label: 'Wolof' },
  { value: 'xh', label: 'Xhosa' },
  { value: 'yi', label: 'Yiddish' },
  { value: 'yo', label: 'Yoruba' },
  { value: 'za', label: 'Zhuang, Chuang' },
  { value: 'zu', label: 'Zulu' }
]
